import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Button, Card, Container, Row, Col, Alert } from 'react-bootstrap';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';
import { FiLogIn, FiMail, FiLock } from 'react-icons/fi';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        // Check if it's a verification error
        if (result.requiresVerification || result.message?.toLowerCase().includes('verify')) {
          setError(result.message || 'Please verify your email before logging in.');
        } else {
          setError(result.message || 'Login failed');
        }
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.requiresVerification) {
        setError(errorData.message || 'Please verify your email before logging in.');
      } else {
        setError(errorData?.message || err.message || 'An error occurred during login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setResendMessage('Please enter your email address first.');
      return;
    }

    setResending(true);
    setResendMessage('');

    try {
      const response = await authApi.resendVerification(email);
      if (response.data.success) {
        setResendMessage(response.data.message || 'Verification email sent! Please check your inbox.');
      }
    } catch (err) {
      setResendMessage(err.response?.data?.message || 'Failed to send verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Container className="mt-5 mb-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <Card className="shadow-lg border-0">
            <Card.Header className="text-center py-4">
              <div className="mb-3">
                <FiLogIn style={{ fontSize: '3rem', color: 'white' }} />
              </div>
              <Card.Title className="mb-0" style={{ color: 'white', fontSize: '1.75rem' }}>
                Welcome Back
              </Card.Title>
              <p className="mb-0 mt-2" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Sign in to your account
              </p>
            </Card.Header>
            <Card.Body className="p-4">
              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                  {error.includes('verify your email') && (
                    <div className="mt-2">
                      <Button 
                        variant="link" 
                        size="sm" 
                        onClick={handleResendVerification}
                        disabled={resending || !email}
                        className="p-0"
                      >
                        {resending ? 'Sending...' : 'Resend verification email'}
                      </Button>
                    </div>
                  )}
                </Alert>
              )}

              {resendMessage && (
                <Alert variant={resendMessage.includes('sent') ? 'success' : 'danger'} className="mb-4">
                  {resendMessage}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4" controlId="email">
                  <Form.Label>
                    <FiMail className="me-2" />
                    Email Address
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="password">
                  <Form.Label>
                    <FiLock className="me-2" />
                    Password
                  </Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>

                <Button 
                  variant="primary" 
                  type="submit" 
                  className="w-100 py-3 mb-3" 
                  disabled={loading}
                  style={{ fontSize: '1.1rem', fontWeight: 600 }}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner me-2"></span>
                      Logging in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </Form>

              <div className="text-center mt-4">
                <p className="text-muted mb-2">Don't have an account?</p>
                <Link to="/signup" style={{ textDecoration: 'none', fontWeight: 600 }}>
                  Create an account
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginPage;
